<template>
  <div class="garden-sidebar">
    <!-- Growth This Week -->
    <div class="garden-sidebar__card">
      <h3 class="garden-sidebar__heading">
        <MTIcon name="bar-chart" :size="16" class="garden-sidebar__icon garden-sidebar__icon--green" />
        Growth This Week
      </h3>
      <div class="garden-sidebar__chart">
        <div v-for="(day, i) in weekDays" :key="day.label" class="garden-sidebar__bar-col">
          <span v-if="day.count > 0" class="garden-sidebar__bar-count">{{ day.count }}</span>
          <div
            class="garden-sidebar__bar"
            :class="{ 'garden-sidebar__bar--today': day.isToday, 'garden-sidebar__bar--future': day.count === 0 && !day.isPast }"
            :style="{ height: barHeight(day.count) }"
          />
          <span class="garden-sidebar__bar-label" :class="{ 'garden-sidebar__bar-label--today': day.isToday }">{{ day.label }}</span>
        </div>
      </div>
      <p class="garden-sidebar__chart-caption">Sessions completed per day</p>
    </div>

    <!-- Activity Calendar -->
    <div class="garden-sidebar__card">
      <h3 class="garden-sidebar__heading">
        <MTIcon name="calendar" :size="16" class="garden-sidebar__icon garden-sidebar__icon--green" />
        Activity Calendar
      </h3>
      <!-- Day-of-week header -->
      <div class="cal-header">
        <span v-for="d in ['M','T','W','T','F','S','S']" :key="d" class="cal-day-label">{{ d }}</span>
      </div>
      <!-- Week rows -->
      <div class="cal-grid">
        <div v-for="(week, wi) in calendarWeeks" :key="wi" class="cal-row">
          <span class="cal-week-label">W{{ wi + 1 }}</span>
          <div
            v-for="(cell, ci) in week" :key="ci"
            class="cal-cell"
            :class="[calCellClass(cell), { 'cal-cell--today': cell.isToday }]"
            :title="cell.date + ': ' + cell.count + ' activities'"
          />
        </div>
      </div>
      <!-- Legend -->
      <div class="cal-footer">
        <p class="cal-footer__summary">{{ activeDayCount }} of {{ totalDayCount }} days active</p>
        <div class="cal-footer__legend">
          <div class="cal-legend-swatch cal-cell--none" /><span class="cal-legend-text">0</span>
          <div class="cal-legend-swatch cal-cell--low" /><span class="cal-legend-text">1</span>
          <div class="cal-legend-swatch cal-cell--mid" /><span class="cal-legend-text">2-3</span>
          <div class="cal-legend-swatch cal-cell--high" /><span class="cal-legend-text">4+</span>
        </div>
      </div>
    </div>

    <!-- Skill Map -->
    <SkillMapWidget :child-id="childId" />

    <!-- Strongest Subjects -->
    <div class="garden-sidebar__card">
      <h3 class="garden-sidebar__heading">
        <MTIcon name="trophy" :size="16" class="garden-sidebar__icon garden-sidebar__icon--amber" />
        Strongest Subjects
      </h3>
      <div class="garden-sidebar__subjects">
        <div v-for="mod in moduleAccuracies" :key="mod.id" class="garden-sidebar__subject">
          <div class="garden-sidebar__subject-header">
            <span class="garden-sidebar__subject-name">{{ mod.title }}</span>
            <span class="garden-sidebar__subject-pct" :class="pctClass(mod.accuracy)">{{ Math.round(mod.accuracy * 100) }}%</span>
          </div>
          <div class="garden-sidebar__progress-track">
            <div class="garden-sidebar__progress-fill" :class="pctFillClass(mod.accuracy)" :style="{ width: `${Math.round(mod.accuracy * 100)}%` }" />
          </div>
        </div>
      </div>
    </div>

    <!-- Recently Bloomed -->
    <div v-if="recentBlooms.length" class="garden-sidebar__card">
      <h3 class="garden-sidebar__heading">
        <MTIcon name="trending-up" :size="16" class="garden-sidebar__icon garden-sidebar__icon--green" />
        Recently Bloomed
      </h3>
      <div class="garden-sidebar__blooms">
        <div v-for="bloom in recentBlooms" :key="bloom.title" class="garden-sidebar__bloom" :class="`garden-sidebar__bloom--${bloom.toBand}`">
          <p class="garden-sidebar__bloom-name">{{ bloom.title }}</p>
          <div class="garden-sidebar__bloom-change">
            <span class="garden-sidebar__bloom-badge" :class="`garden-sidebar__bloom-badge--${bloom.fromBand}`">{{ bandLabel(bloom.fromBand) }}</span>
            <span class="garden-sidebar__bloom-arrow">&rarr;</span>
            <span class="garden-sidebar__bloom-badge" :class="`garden-sidebar__bloom-badge--${bloom.toBand}`">{{ bandLabel(bloom.toBand) }}</span>
            <span class="garden-sidebar__bloom-up">&uarr;</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import MTIcon from '@/components/ui/MTIcon.vue';
import { useProgressStore } from '@/stores/progressStore';
import { useCurriculumStore } from '@/stores/curriculumStore';
import SkillMapWidget from './SkillMapWidget.vue';

const props = defineProps({
  childId: { type: String, required: true }
});

const progressStore = useProgressStore();
const curriculumStore = useCurriculumStore();

// Week days bar chart data
const weekDays = computed(() => {
  const log = progressStore.activityLog?.[props.childId] || [];
  const now = new Date();
  const today = now.getDay(); // 0=Sun
  // Get Monday of current week
  const monday = new Date(now);
  const daysSinceMonday = today === 0 ? 6 : today - 1;
  monday.setDate(monday.getDate() - daysSinceMonday);
  monday.setHours(0, 0, 0, 0);

  const labels = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  const days = [];

  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setDate(d.getDate() + i);
    const dayKey = d.toISOString().slice(0, 10);
    const todayKey = now.toISOString().slice(0, 10);
    const SESSION_TYPES = ['lesson', 'revision', 'diagnostic'];
    const count = log.filter(e => {
      if (!SESSION_TYPES.includes(e.type)) return false;
      const eKey = new Date(e.timestamp).toISOString().slice(0, 10);
      return eKey === dayKey;
    }).length;
    days.push({
      label: labels[i],
      count,
      isToday: dayKey === todayKey,
      isPast: d < now
    });
  }
  return days;
});

const chartHeight = 80; // px available for bars (100px minus label space)
function barHeight(count) {
  const max = Math.max(...weekDays.value.map(d => d.count), 1);
  if (count === 0) return '4px';
  return `${Math.max(Math.round((count / max) * chartHeight), 6)}px`;
}

// Activity Calendar — last 4 weeks (Mon-Sun rows)
const calendarWeeks = computed(() => {
  const log = progressStore.activityLog?.[props.childId] || [];
  const now = new Date();
  const today = now.getDay(); // 0=Sun
  // Find Monday of the current week
  const currentMonday = new Date(now);
  const daysSinceMon = today === 0 ? 6 : today - 1;
  currentMonday.setDate(currentMonday.getDate() - daysSinceMon);
  currentMonday.setHours(0, 0, 0, 0);

  // Go back 3 more weeks to get 4 weeks total
  const startMonday = new Date(currentMonday);
  startMonday.setDate(startMonday.getDate() - 21);

  // Build a count map by date key (sessions only)
  const SESSION_TYPES = ['lesson', 'revision', 'diagnostic'];
  const countMap = {};
  for (const e of log) {
    if (!SESSION_TYPES.includes(e.type)) continue;
    const key = new Date(e.timestamp).toISOString().slice(0, 10);
    countMap[key] = (countMap[key] || 0) + 1;
  }

  const todayKey = now.toISOString().slice(0, 10);
  const weeks = [];
  for (let w = 0; w < 4; w++) {
    const week = [];
    for (let d = 0; d < 7; d++) {
      const date = new Date(startMonday);
      date.setDate(date.getDate() + w * 7 + d);
      const key = date.toISOString().slice(0, 10);
      week.push({ date: key, count: countMap[key] || 0, isToday: key === todayKey });
    }
    weeks.push(week);
  }
  return weeks;
});

const activeDayCount = computed(() => {
  return calendarWeeks.value.flat().filter(c => c.count > 0).length;
});
const totalDayCount = computed(() => calendarWeeks.value.flat().length);

function calCellClass(cell) {
  if (cell.count === 0) return 'cal-cell--none';
  if (cell.count === 1) return 'cal-cell--low';
  if (cell.count <= 3) return 'cal-cell--mid';
  return 'cal-cell--high';
}

// Module-level accuracy averages
const moduleAccuracies = computed(() => {
  const sgs = curriculumStore.allSectionGroups;
  const childProgress = progressStore.progress?.[props.childId] || {};
  const modMap = {};

  for (const sg of sgs) {
    if (!modMap[sg.moduleId]) {
      modMap[sg.moduleId] = { id: sg.moduleId, title: sg.moduleTitle, total: 0, sum: 0 };
    }
    const p = childProgress[sg.sectionGroupId];
    if (p && typeof p.accuracy === 'number') {
      modMap[sg.moduleId].total++;
      modMap[sg.moduleId].sum += p.accuracy;
    }
  }

  return Object.values(modMap)
    .filter(m => m.total > 0)
    .map(m => ({ ...m, accuracy: m.sum / m.total }))
    .sort((a, b) => b.accuracy - a.accuracy);
});

function pctClass(acc) {
  if (acc >= 0.75) return 'garden-sidebar__subject-pct--high';
  if (acc >= 0.5) return 'garden-sidebar__subject-pct--mid';
  return 'garden-sidebar__subject-pct--low';
}
function pctFillClass(acc) {
  if (acc >= 0.75) return 'garden-sidebar__progress-fill--high';
  if (acc >= 0.5) return 'garden-sidebar__progress-fill--mid';
  return 'garden-sidebar__progress-fill--low';
}

// Recently bloomed: look for band-change events in activity log
const bandOrder = ['beginner', 'developing', 'secure', 'confident', 'mastery'];
const recentBlooms = computed(() => {
  const log = progressStore.activityLog?.[props.childId] || [];
  // Find badge events that indicate band promotion
  return log
    .filter(e => e.type === 'badge' && e.fromBand && e.toBand)
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, 5);
});

function bandLabel(band) {
  const labels = { beginner: 'Beginner', developing: 'Developing', secure: 'Secure', confident: 'Confident', mastery: 'Mastery' };
  return labels[band] || band;
}
</script>

<style scoped>
.garden-sidebar {
  width: 100%;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}
.garden-sidebar__card {
  background: white;
  border-radius: 1rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  padding: 1.25rem;
}
.garden-sidebar__heading {
  font-weight: 700;
  color: var(--mt-text-primary, #1f2937);
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
}
.garden-sidebar__icon--green { color: #059669; }
.garden-sidebar__icon--amber { color: #d97706; }

/* Bar chart */
.garden-sidebar__chart {
  display: flex;
  justify-content: space-between;
  gap: 0.25rem;
  height: 100px;
}
.garden-sidebar__bar-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  height: 100%;
}
.garden-sidebar__bar-count {
  font-size: 0.625rem;
  font-weight: 700;
  color: #059669;
  margin-bottom: 0.125rem;
}
.garden-sidebar__bar {
  width: 100%;
  background: #34d399;
  border-radius: 0.25rem 0.25rem 0 0;
  transition: height 0.6s ease;
}
.garden-sidebar__bar--today {
  background: #059669;
  box-shadow: 0 0 0 2px #6ee7b7;
}
.garden-sidebar__bar--future {
  background: #e5e7eb;
}
.garden-sidebar__bar-label {
  font-size: 0.75rem;
  color: #9ca3af;
  margin-top: 0.25rem;
}
.garden-sidebar__bar-label--today {
  font-weight: 700;
  color: #059669;
}
.garden-sidebar__chart-caption {
  font-size: 0.75rem;
  color: #9ca3af;
  text-align: center;
  margin-top: 0.5rem;
}

/* Activity Calendar */
.cal-header {
  display: flex;
  gap: 0.25rem;
  margin-bottom: 0.25rem;
  margin-left: 1.75rem;
}
.cal-day-label {
  width: 1.25rem;
  text-align: center;
  font-size: 0.625rem;
  color: #9ca3af;
}
.cal-grid { display: flex; flex-direction: column; gap: 0.25rem; }
.cal-row { display: flex; align-items: center; gap: 0.25rem; }
.cal-week-label {
  width: 1.5rem;
  text-align: right;
  font-size: 0.625rem;
  color: #9ca3af;
}
.cal-cell {
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 0.25rem;
}
.cal-cell--none { background: #e5e7eb; }
.cal-cell--low { background: #86efac; }
.cal-cell--mid { background: #22c55e; }
.cal-cell--high { background: #15803d; }
.cal-cell--today { outline: 2px solid #059669; outline-offset: 1px; }
.cal-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 0.75rem;
}
.cal-footer__summary { font-size: 0.75rem; color: #6b7280; font-weight: 600; }
.cal-footer__legend { display: flex; align-items: center; gap: 0.25rem; }
.cal-legend-swatch { width: 0.75rem; height: 0.75rem; border-radius: 0.125rem; }
.cal-legend-text { font-size: 0.5625rem; color: #9ca3af; }

/* Subjects */
.garden-sidebar__subjects { display: flex; flex-direction: column; gap: 0.75rem; }
.garden-sidebar__subject-header { display: flex; justify-content: space-between; font-size: 0.875rem; margin-bottom: 0.25rem; }
.garden-sidebar__subject-name { font-weight: 600; color: var(--mt-text-secondary, #374151); }
.garden-sidebar__subject-pct { font-weight: 700; }
.garden-sidebar__subject-pct--high { color: #059669; }
.garden-sidebar__subject-pct--mid { color: #ca8a04; }
.garden-sidebar__subject-pct--low { color: #ea580c; }
.garden-sidebar__progress-track { width: 100%; background: #f3f4f6; border-radius: 9999px; height: 0.625rem; }
.garden-sidebar__progress-fill { height: 0.625rem; border-radius: 9999px; transition: width 0.6s ease; }
.garden-sidebar__progress-fill--high { background: #10b981; }
.garden-sidebar__progress-fill--mid { background: #facc15; }
.garden-sidebar__progress-fill--low { background: #fb923c; }

/* Blooms */
.garden-sidebar__blooms { display: flex; flex-direction: column; gap: 0.75rem; }
.garden-sidebar__bloom {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  border-radius: 0.75rem;
  padding: 0.75rem;
}
.garden-sidebar__bloom--developing { background: #ecfdf5; }
.garden-sidebar__bloom--secure { background: #fefce8; }
.garden-sidebar__bloom--confident { background: #fff7ed; }
.garden-sidebar__bloom--mastery { background: #faf5ff; }
.garden-sidebar__bloom-name { font-weight: 700; font-size: 0.875rem; color: var(--mt-text-primary, #1f2937); }
.garden-sidebar__bloom-change { display: flex; align-items: center; gap: 0.375rem; margin-top: 0.25rem; }
.garden-sidebar__bloom-badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
}
.garden-sidebar__bloom-badge--beginner { background: #dbeafe; color: #1d4ed8; }
.garden-sidebar__bloom-badge--developing { background: #dcfce7; color: #15803d; }
.garden-sidebar__bloom-badge--secure { background: #fef9c3; color: #a16207; }
.garden-sidebar__bloom-badge--confident { background: #ffedd5; color: #c2410c; }
.garden-sidebar__bloom-badge--mastery { background: #f3e8ff; color: #7e22ce; }
.garden-sidebar__bloom-arrow { font-size: 0.75rem; font-weight: 700; color: #10b981; }
.garden-sidebar__bloom-up { font-weight: 700; color: #10b981; }
</style>

<!-- Theme overrides (non-scoped) -->
<style>
.theme-space .garden-sidebar__card {
  background: rgba(30, 41, 59, 0.8);
  border: 1px solid rgba(52, 211, 153, 0.15);
}
.theme-space .garden-sidebar__heading { color: #d1fae5; }
.theme-space .garden-sidebar__bar-count { color: #6ee7b7; }
.theme-space .garden-sidebar__bar { background: #34d399; }
.theme-space .garden-sidebar__bar--today { background: #6ee7b7; box-shadow: 0 0 0 2px rgba(110, 231, 183, 0.3); }
.theme-space .garden-sidebar__bar--future { background: #334155; }
.theme-space .garden-sidebar__bar-label { color: #64748b; }
.theme-space .garden-sidebar__bar-label--today { color: #6ee7b7; }
.theme-space .garden-sidebar__chart-caption { color: #64748b; }
.theme-space .garden-sidebar__subject-name { color: #cbd5e1; }
.theme-space .cal-day-label,
.theme-space .cal-week-label { color: #475569; }
.theme-space .cal-cell--none { background: #334155; }
.theme-space .cal-cell--low { background: #065f46; }
.theme-space .cal-cell--mid { background: #059669; }
.theme-space .cal-cell--high { background: #34d399; box-shadow: 0 0 4px rgba(52,211,153,0.4); }
.theme-space .cal-cell--today { outline-color: #6ee7b7; }
.theme-space .cal-footer__summary { color: #94a3b8; }
.theme-space .cal-legend-text { color: #64748b; }
.theme-space .garden-sidebar__progress-track { background: #334155; }
.theme-space .garden-sidebar__bloom--developing { background: rgba(16, 185, 129, 0.1); }
.theme-space .garden-sidebar__bloom--secure { background: rgba(250, 204, 21, 0.1); }
.theme-space .garden-sidebar__bloom--confident { background: rgba(249, 115, 22, 0.1); }
.theme-space .garden-sidebar__bloom--mastery { background: rgba(168, 85, 247, 0.1); }
.theme-space .garden-sidebar__bloom-name { color: #e2e8f0; }

.theme-trail .garden-sidebar__card { background: #fefce8; border: 1px solid #d6c89e; }
.theme-road .garden-sidebar__card { background: #f0fdfa; border: 1px solid #99f6e4; }
</style>
